import type { ReportError } from "../types/index.js";

const AUDIT_CODES = new Set([
  "audit_failed",
  "audit_timeout",
  "audit_upstream_error",
  "audit_unreachable",
  "audit_rate_limited",
  "audit_empty_response",
  "audit_invalid_response",
  "audit_unauthorized",
]);

/**
 * What a stored error means for the reader, in one sentence, without the provider's vocabulary.
 * A partial report says what is missing and why in a person's terms; a status code or an upstream
 * name is for the log, never for the page.
 */
export function explainReportError(error: ReportError): string {
  if (AUDIT_CODES.has(error.code)) {
    const because =
      error.code === "audit_timeout"
        ? "it took too long"
        : error.code === "audit_rate_limited"
          ? "the service was busy"
          : error.code === "audit_unreachable"
            ? "the service could not be reached"
            : "the service did not answer";
    return `The WordLift foundation audit did not complete because ${because}, so there is no foundation score this time. Run again to try it once more.`;
  }
  switch (error.code) {
    case "collection_timeout":
      return "The site took too long to answer, so its pages could not be read.";
    case "collector_failed":
      return "The site's pages could not be read.";
    case "classifier_unavailable":
      return `Classification fell back to observed behavior: ${error.message}`;
    default:
      return error.message;
  }
}

const COLLECTION_CODES = new Set(["site_blocked", "collection_timeout", "collector_failed", "dns_failure"]);

/**
 * The errors worth a reader's attention. When the site could not be read at all, the classifier's
 * complaint about missing text says nothing the collection error has not already said.
 */
export function visibleErrors(errors: ReportError[]): ReportError[] {
  const collectionFailed = errors.some((error) => COLLECTION_CODES.has(error.code));
  return collectionFailed ? errors.filter((error) => error.code !== "classifier_unavailable") : errors;
}

/**
 * Whether all that went wrong is the foundation audit not answering: the model and what agents can
 * do are whole, only the foundation score is missing. That is a line beside the score, not a banner
 * above the business.
 */
export function onlyFoundationMissing(errors: ReportError[]): boolean {
  const visible = visibleErrors(errors).filter((error) => error.code !== "classifier_unavailable");
  return visible.length > 0 && visible.every((error) => error.provider === "wordlift-ai-audit");
}

/** What stopped the site's pages from being read at all: the collector, or a destination the service refuses. */
const UNREADABLE_CODES = new Set(["site_blocked", "collection_timeout", "collector_failed", "dns_failure", "private_network", "too_many_redirects", "response_too_large", "unsupported_port", "invalid_url"]);

/** The reason a site could not be read, when that is what happened; null when its pages were read. */
export function unreadableReason(errors: ReportError[]): ReportError | null {
  return errors.find((error) => UNREADABLE_CODES.has(error.code)) ?? null;
}

/** A heading for a report that could not be built, chosen by what actually went wrong. */
export function failureTitle(errors: ReportError[]): string {
  const codes = new Set(errors.map((error) => error.code));
  if (codes.has("site_blocked")) return "This site blocks automated access";
  if (codes.has("collection_timeout")) return "This site took too long to answer";
  if (codes.has("dns_failure")) return "This site could not be reached";
  return "We could not understand this site";
}
